import {
  createSlotRecipeContext,
  Icon,
  type HTMLChakraProps,
  type RecipeVariantProps,
} from '@chakra-ui/react';
import { isValidElement } from 'react';

import { noticeSlotRecipe } from '../../theme/slot-recipes/notice';

const { withProvider, withContext } = createSlotRecipeContext({ recipe: noticeSlotRecipe });

type NoticeVariants = RecipeVariantProps<typeof noticeSlotRecipe>;

const NoticeRoot = withProvider<HTMLDivElement, HTMLChakraProps<'div'> & NoticeVariants>(
  'div',
  'root'
);
const NoticeIcon = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'icon');
const NoticeContent = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'content');
const NoticeTitle = withContext<HTMLParagraphElement, HTMLChakraProps<'p'>>('p', 'title');
const NoticeDescription = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'description');
const NoticeActions = withContext<HTMLDivElement, HTMLChakraProps<'div'>>('div', 'actions');

type NoticeProps = Omit<HTMLChakraProps<'div'>, 'title'> & {
  /** Что человеку с этим делать. Смысл тонов - в `theme/slot-recipes/notice.ts`. */
  tone?: 'info' | 'warning' | 'danger' | 'accent';
  /**
   * `sm` - строка-подсказка внутри другого блока: без плитки под значком и без рамки.
   * `bar` - полоса во всю ширину окна под шапкой, про состояние всего приложения.
   */
  size?: 'md' | 'sm' | 'bar';
  /** Move the whole action row below the copy when the notice's container is narrow. */
  actionLayout?: 'adaptive';
  /** Компонент значка или готовый элемент. */
  icon?: React.ElementType | React.ReactElement;
  title?: React.ReactNode;
  /** Пояснение под заголовком. Может быть не только текстом: картинкой, ссылкой, списком. */
  children?: React.ReactNode;
  /** Кнопки справа. На узком экране уходят под текст во всю ширину. */
  action?: React.ReactNode;
};

/**
 * Плашка: сообщение, которое экран говорит сам. Правила тонов и почему заливка прозрачная - в
 * рецепте; здесь только раскладка по слотам.
 *
 * `role` не ставится: плашки статичны и видны сразу, а `role="alert"` заставил бы читалку
 * прерывать человека на каждой из них при открытии экрана. Там, где плашка появляется в ответ на
 * действие, роль передаёт вызывающий.
 */
export const Notice = ({
  tone,
  size,
  actionLayout,
  icon,
  title,
  children,
  action,
  ...rest
}: NoticeProps) => (
  <NoticeRoot tone={tone} size={size} actionLayout={actionLayout} {...rest}>
    {icon ? (
      <NoticeIcon aria-hidden>{isValidElement(icon) ? icon : <Icon as={icon} />}</NoticeIcon>
    ) : null}
    <NoticeContent>
      {title ? <NoticeTitle>{title}</NoticeTitle> : null}
      {children ? <NoticeDescription>{children}</NoticeDescription> : null}
    </NoticeContent>
    {action ? <NoticeActions>{action}</NoticeActions> : null}
  </NoticeRoot>
);
